import type { Metadata } from "next";
import { routing } from "@/i18n/routing";

const SITE_NAME = "Geesol";
const FALLBACK_BASE_URL = "https://geesol.com";
const previewDomains = [".netlify.app", ".vercel.app"];
const localHosts = new Set(["localhost", "127.0.0.1", "0.0.0.0", "::1", "[::1]"]);

const normalizeBaseUrl = (rawValue?: string) => {
  if (!rawValue) return null;
  const value = rawValue.trim();
  if (!value) return null;

  const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;

  try {
    const url = new URL(withProtocol);
    const hostname = url.hostname.toLowerCase();

    if (localHosts.has(hostname) || hostname.endsWith(".localhost")) return null;
    if (previewDomains.some((domain) => hostname.endsWith(domain))) return null;

    url.hash = "";
    url.search = "";
    url.pathname = "";
    return url.toString().replace(/\/$/, "");
  } catch {
    return null;
  }
};

const resolvedBaseUrl =
  normalizeBaseUrl(process.env.NEXT_PUBLIC_BASE_URL) ??
  normalizeBaseUrl(process.env.SITE_URL) ??
  normalizeBaseUrl(process.env.URL) ??
  FALLBACK_BASE_URL;

export const localeHrefLangs: Record<string, string> = {
  es: "es-ES",
  en: "en-US",
};

export const buildSocialTitle = (pageTitle: string) => {
  const fullBrand = SITE_NAME;
  const normalizedTitle = pageTitle?.trim();
  if (!normalizedTitle) return fullBrand;
  // Avoid duplicating the brand when the page title already contains it
  if (normalizedTitle.includes(SITE_NAME)) return normalizedTitle;
  return `${normalizedTitle} | ${fullBrand}`;
};

const ogLocales: Record<string, string> = {
  es: "es_ES",
  en: "en_US",
};

type OpenGraphType =
  | "website"
  | "article"
  | "book"
  | "profile"
  | "music.song"
  | "music.album"
  | "music.playlist"
  | "music.radio_station"
  | "video.movie"
  | "video.episode"
  | "video.tv_show"
  | "video.other";
const defaultOpenGraphType: OpenGraphType = "website";

type MetadataOptions = {
  title: string;
  description: string;
  path?: string;
  locale?: string;
  image?: string;
  type?: OpenGraphType;
  noIndex?: boolean;
};

const normalizePath = (path?: string) => {
  if (!path || path === "/") return "";
  return path.startsWith("/") ? path : `/${path}`;
};

const localePrefix = (locale?: string) => {
  if (!locale || locale === routing.defaultLocale) return "";
  return `/${locale}`;
};

export const localizedPath = (path?: string, locale?: string) => {
  return `${localePrefix(locale)}${normalizePath(path)}`;
};

export const absoluteUrl = (path?: string, locale?: string) => {
  return `${resolvedBaseUrl}${localizedPath(path, locale)}`;
};

export function createPageMetadata({
  title,
  description,
  path,
  locale,
  image = "/og-image.png",
  type = defaultOpenGraphType,
  noIndex = false,
}: MetadataOptions): Metadata {
  const url = absoluteUrl(path, locale);
  const imageUrl = image.startsWith("http") ? image : `${resolvedBaseUrl}${normalizePath(image)}`;
  const ogLocale = locale ? ogLocales[locale] ?? locale : undefined;
  const socialTitle = buildSocialTitle(title);

  const alternateLanguages = Object.fromEntries(
    routing.locales.map((loc) => [localeHrefLangs[loc] ?? loc, absoluteUrl(path, loc)])
  );
  alternateLanguages["x-default"] = absoluteUrl(path, routing.defaultLocale);

  return {
    title,
    description,
    keywords: title,
    ...(noIndex && { robots: { index: false, follow: false } }),
    alternates: { canonical: url, languages: alternateLanguages },
    openGraph: {
      title: socialTitle,
      description,
      url,
      siteName: SITE_NAME,
      type: type as OpenGraphType,
      locale: ogLocale,
      images: [{ url: imageUrl, width: 1200, height: 630, alt: title }],
    },
    twitter: {
      card: "summary_large_image",
      title: socialTitle,
      description,
      images: [imageUrl],
    },
  };
}

export { resolvedBaseUrl as metadataBaseUrl, SITE_NAME as siteName };
